import { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { XMarkIcon, CloudArrowUpIcon, DocumentIcon, ExclamationTriangleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import type { Upload as TusUpload } from 'tus-js-client'
import { submitJob, uploadLimits } from '../services/api'
import {
  createInstallerUpload,
  uploadErrorDetail,
  uploadErrorStatus,
  type UploadLimits,
} from '../services/installerUpload'
import { installerCandidates, preselectedInstaller, readZipListing, type ZipEntry } from '../services/zipListing'
import { errorText } from '../utils/errorText'
import { plural } from '../utils/plural'
import PackagingEntitlementNotice, { EntitlementRefusalAction, isEntitlementRefusal } from './PackagingEntitlementNotice'

interface AddInstallerModalProps {
  isOpen: boolean
  onClose: () => void
  /** Upload new version: the uploaded app this installer is a newer version of. The job packages
   *  into that app; the server checks that it is the same product and a newer version. */
  targetApp?: { id: string; name: string; version?: string | null }
}

// Supported installer file extensions
const SUPPORTED_EXTENSIONS = ['.exe', '.msi', '.msix']

const ZIP_EXTENSION = '.zip'
const OTHER_ARCHIVES = ['.7z', '.rar', '.tar', '.gz', '.tgz', '.bz2', '.xz', '.cab', '.iso']

/** What the dialog knows about a selected zip's installers, before anything is uploaded */
type ZipState =
  | { status: 'none' }
  | { status: 'reading' }
  | { status: 'ready'; candidates: ZipEntry[] }
  | { status: 'unusable'; message: string }

type UploadState = 'idle' | 'uploading' | 'submitting' | 'complete' | 'error'
type PackagingMode = 'ai' | 'manual'

/** What the user can do about an error: resume the upload, re-submit the job, or start over. */
type Recovery = 'resume' | 'resubmit' | 'restart' | 'none'

interface UploadProgress {
  bytesSent: number
  bytesTotal: number
  /** bytes per second over the recent window; null until there is enough to say */
  rate: number | null
}

/** How far back the transfer rate looks, so a stall shows as one within seconds. */
const RATE_WINDOW_MS = 15_000

// Manual mode parameters matching AEAM CLI options
interface ManualModeParams {
  appName: string
  appVersion: string
  installParams: string
  appPublisher: string
  installerType: 'auto' | 'exe' | 'msi' | 'msix'
  uninstallMethod: 'uninstall_string' | 'msi_product_code' | 'custom'
  uninstallParams: string
  uninstallExe: string
  productCode: string
  detectionType: 'registry' | 'file' | 'msi_product_code'
  detectionPath: string
  detectionValue: string
  detectionExpected: string
  closeApps: string
}

const defaultManualParams: ManualModeParams = {
  appName: '',
  appVersion: '',
  installParams: '',
  appPublisher: '',
  installerType: 'auto',
  uninstallMethod: 'uninstall_string',
  uninstallParams: '',
  uninstallExe: '',
  productCode: '',
  detectionType: 'registry',
  detectionPath: '',
  detectionValue: 'DisplayName',
  detectionExpected: '',
  closeApps: ''
}

export default function AddInstallerModal({ isOpen, onClose, targetApp }: AddInstallerModalProps) {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Form state
  const [file, setFile] = useState<File | null>(null)
  const [appName, setAppName] = useState('')
  const [enableAgentic, setEnableAgentic] = useState(true)
  const [installTimeout, setInstallTimeout] = useState<number | undefined>(undefined)

  // Packaging mode state
  const [packagingMode, setPackagingMode] = useState<PackagingMode>('ai')
  const [manualParams, setManualParams] = useState<ManualModeParams>(defaultManualParams)
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false)

  // UI state
  const [uploadState, setUploadState] = useState<UploadState>('idle')
  const [progress, setProgress] = useState<UploadProgress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [recovery, setRecovery] = useState<Recovery>('none')
  const [success, setSuccess] = useState<{ jobId: string; appId: string } | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [zip, setZip] = useState<ZipState>({ status: 'none' })
  const [installerEntry, setInstallerEntry] = useState('')
  // The service's limits: the largest installer it takes and its chunk size. Null until read.
  const [limits, setLimits] = useState<UploadLimits | null>(null)

  // The upload in flight, kept out of React state: the same object resumes on Try again
  const uploadRef = useRef<TusUpload | null>(null)
  const uploadIdRef = useRef<string | null>(null)
  const rateSamplesRef = useRef<Array<{ at: number; bytes: number }>>([])
  const errorRef = useRef<HTMLDivElement>(null)

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFile(null)
      setAppName('')
      setEnableAgentic(true)
      setInstallTimeout(undefined)
      setPackagingMode('ai')
      setManualParams(defaultManualParams)
      setShowAdvancedOptions(false)
      setUploadState('idle')
      setProgress(null)
      setError(null)
      setRecovery('none')
      setSuccess(null)
      setIsDragOver(false)
      setZip({ status: 'none' })
      setInstallerEntry('')
      uploadRef.current = null
      uploadIdRef.current = null
    }
  }, [isOpen])

  // The limit comes from the service, so the dialog never promises a size the service refuses
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    uploadLimits()
      .then((l) => { if (!cancelled) setLimits(l) })
      .catch(() => { /* the service enforces the limit at creation either way */ })
    return () => { cancelled = true }
  }, [isOpen])

  // An error is shown where the user is looking
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [error])

  // Update manual params helper
  const updateManualParam = <K extends keyof ManualModeParams>(key: K, value: ManualModeParams[K]) => {
    setManualParams(prev => ({ ...prev, [key]: value }))
  }

  const supportedExtensions = limits?.fileTypes ?? SUPPORTED_EXTENSIONS
  const zipAccepted = supportedExtensions.includes(ZIP_EXTENSION)
  const isZip = zip.status !== 'none'
  const chosenIsMsi = isZip && installerEntry.toLowerCase().endsWith('.msi')

  // File validation
  const validateFile = useCallback((file: File): string | null => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase()

    if (!supportedExtensions.includes(extension)) {
      if (zipAccepted && OTHER_ARCHIVES.includes(extension)) {
        return `${extension} archives cannot be packaged. Upload the files as a .zip instead.`
      }
      return `Unsupported file type "${extension}". Please upload ${supportedExtensions.join(', ')} files.`
    }

    if (limits && file.size > limits.maxBytes) {
      return `File too large (${formatFileSize(file.size)}). The maximum size is ${formatFileSize(limits.maxBytes)}.`
    }

    if (file.size === 0) {
      return 'File is empty. Please select a valid installer file.'
    }

    return null
  }, [limits, supportedExtensions, zipAccepted])

  // The zip's installers, read in the browser so the user chooses before anything is uploaded
  const readZip = useCallback(async (selected: File) => {
    setZip({ status: 'reading' })
    setInstallerEntry('')
    try {
      const listing = await readZipListing(selected)
      if (listing.encrypted) {
        setZip({ status: 'unusable', message: 'This zip is protected by a password. Upload a zip without a password.' })
      } else if (listing.legacyName) {
        setZip({
          status: 'unusable',
          message: 'A file name in this zip is not stored as UTF-8. Create the zip again with a tool that writes UTF-8 names, such as 7-Zip.',
        })
      } else {
        const candidates = installerCandidates(listing.entries)
        if (candidates.length === 0) {
          setZip({
            status: 'unusable',
            message: 'This zip holds no .msi or .exe installer. Portable apps cannot be uploaded in a zip.',
          })
        } else {
          setZip({ status: 'ready', candidates })
          setInstallerEntry(preselectedInstaller(candidates))
        }
      }
    } catch (err) {
      setZip({ status: 'unusable', message: errorText(err, 'This file is not a readable zip.') })
    }
  }, [])

  // Handle file selection
  const handleFileSelect = useCallback((selectedFile: File) => {
    setError(null)

    const validationError = validateFile(selectedFile)
    if (validationError) {
      setError(validationError)
      return
    }

    setFile(selectedFile)
    uploadIdRef.current = null
    if (selectedFile.name.toLowerCase().endsWith(ZIP_EXTENSION)) {
      void readZip(selectedFile)
    } else {
      setZip({ status: 'none' })
      setInstallerEntry('')
    }
    // Auto-fill app name from filename (without extension)
    const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '')
    if (!appName) {
      setAppName(nameWithoutExt)
    }
    // Also update manual params app name if empty
    if (!manualParams.appName) {
      updateManualParam('appName', nameWithoutExt)
      updateManualParam('detectionExpected', nameWithoutExt)
    }
    // Auto-detect installer type from extension
    const ext = selectedFile.name.split('.').pop()?.toLowerCase()
    if (ext === 'msi') {
      updateManualParam('installerType', 'msi')
    } else if (ext === 'msix') {
      updateManualParam('installerType', 'msix')
    } else {
      updateManualParam('installerType', 'exe')
    }
  }, [appName, manualParams.appName, validateFile, readZip])

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      handleFileSelect(droppedFile)
    }
  }, [handleFileSelect])

  // Click to browse
  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      handleFileSelect(selectedFile)
    }
  }, [handleFileSelect])

  // Remove selected file
  const handleRemoveFile = useCallback(() => {
    setFile(null)
    setAppName('')
    setError(null)
    setZip({ status: 'none' })
    setInstallerEntry('')
    uploadIdRef.current = null
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  // Validate manual mode params
  const validateManualParams = (): string | null => {
    if (!manualParams.appName.trim()) {
      return 'Package name is required in manual mode'
    }
    if (!manualParams.appVersion.trim()) {
      return 'Package version is required in manual mode'
    }
    if (!manualParams.installParams.trim()) {
      return 'Install Parameters are required in manual mode'
    }
    // An MSI chosen from a zip carries its own product code, which AEAM reads
    if (manualParams.uninstallMethod === 'msi_product_code' && !manualParams.productCode.trim() && !chosenIsMsi) {
      return 'Product Code is required when using MSI Product Code uninstall method'
    }
    return null
  }

  // What the job endpoint needs besides the installer
  const jobSettings = () => ({
    installer_entry: isZip ? installerEntry : undefined,
    app_name: targetApp ? targetApp.name : packagingMode === 'ai' ? appName.trim() : manualParams.appName.trim(),
    ...(targetApp ? { target_app_id: targetApp.id } : {}),
    enable_agentic: packagingMode === 'ai' ? enableAgentic : false,
    install_timeout: installTimeout,
    manual_params: packagingMode === 'manual' ? {
      app_name: manualParams.appName,
      app_version: manualParams.appVersion,
      install_params: manualParams.installParams,
      app_publisher: manualParams.appPublisher || undefined,
      // For a zip, the chosen file decides the type
      installer_type: !isZip && manualParams.installerType !== 'auto' ? manualParams.installerType : undefined,
      uninstall_method: manualParams.uninstallMethod,
      uninstall_params: manualParams.uninstallParams || undefined,
      uninstall_exe: manualParams.uninstallExe || undefined,
      product_code: manualParams.productCode || undefined,
      detection_type: manualParams.detectionType,
      detection_path: manualParams.detectionPath || undefined,
      detection_value: manualParams.detectionValue || undefined,
      detection_expected: manualParams.detectionExpected || manualParams.appName,
      close_apps: manualParams.closeApps || undefined,
    } : undefined,
  })

  const fail = (message: string, how: Recovery) => {
    setUploadState('error')
    setError(message)
    setRecovery(how)
  }

  // The transfer rate over the recent window, from the bytes the client has sent
  const noteProgress = (bytesSent: number, bytesTotal: number) => {
    const now = Date.now()
    const samples = rateSamplesRef.current
    samples.push({ at: now, bytes: bytesSent })
    while (samples.length > 1 && now - samples[0].at > RATE_WINDOW_MS) samples.shift()
    const first = samples[0]
    const elapsed = (now - first.at) / 1000
    const rate = elapsed >= 1 && bytesSent >= first.bytes ? (bytesSent - first.bytes) / elapsed : null
    setProgress({ bytesSent, bytesTotal, rate })
  }

  // The packaging job, from the finished upload
  const submitFromUpload = async (uploadId: string) => {
    setUploadState('submitting')
    setError(null)
    try {
      const result = await submitJob({
        source_type: 'file_upload',
        deployment_methods: ['psadt', 'intune'],
        upload_id: uploadId,
        ...jobSettings(),
      })
      setUploadState('complete')
      setSuccess({ jobId: result.job_id, appId: result.app_id || '' })
    } catch (err: any) {
      if (err.response?.status === 409) {
        const existingJobId = err.response.headers?.['x-duplicate-job-id']
        fail(`This file has already been submitted for packaging.${existingJobId ? ` Existing Job ID: ${existingJobId}` : ''} Please wait for the existing job to complete or use a different file.`, 'none')
      } else if (err.response?.status === 410) {
        fail('The upload expired before the job was created. Start the upload again.', 'restart')
      } else if (err.response?.status === 400 && isZip) {
        // The zip itself or the choice must change, so repeating the same request cannot help
        fail(errorText(err, 'This zip cannot be packaged.'), 'none')
      } else {
        // The upload is intact; only the job submission is repeated
        fail(errorText(err, 'The packaging job could not be created. Please try again.'), 'resubmit')
      }
    }
  }

  // What went wrong with the upload, in words, and what can be done about it
  const uploadFailed = (err: Error) => {
    const status = uploadErrorStatus(err)
    const detail = uploadErrorDetail(err)
    if (status === 413) {
      fail(detail || `File too large. The maximum size is ${limits ? formatFileSize(limits.maxBytes) : 'the service limit'}.`, 'none')
    } else if (status === 403 || status === 429 || status === 400) {
      fail(detail || errorText(err, 'The upload was refused.'), 'none')
    } else if (status === 410 || status === 404) {
      fail('The upload expired before it finished. Start the upload again.', 'restart')
    } else if (status === 401) {
      fail('Your session has expired. Sign in again, then try again to continue the upload.', 'resume')
    } else if (status === 503) {
      fail(detail || 'The platform cannot store uploads right now. Please try again later.', 'resume')
    } else if (status >= 500) {
      fail('The upload service answered with an error. Try again to continue from where it stopped.', 'resume')
    } else {
      fail('The connection could not be restored. Check your network and try again to continue the upload.', 'resume')
    }
  }

  // Start Packaging: create the upload, send the chunks, then submit the job
  const handleSubmit = async () => {
    if (!file) {
      setError('Please select a file')
      return
    }
    if (isZip && !installerEntry) {
      setError('Choose the installer to run from this zip')
      return
    }

    // Validate based on mode
    if (packagingMode === 'ai') {
      if (!appName.trim()) {
        setError('Please enter an app name')
        return
      }
    } else {
      const manualError = validateManualParams()
      if (manualError) {
        setError(manualError)
        return
      }
    }

    // A zip the service refused is already uploaded: only the job is submitted again
    if (isZip && uploadIdRef.current) {
      await submitFromUpload(uploadIdRef.current)
      return
    }

    let chunkBytes = limits?.chunkBytes
    if (!chunkBytes) {
      try {
        const l = await uploadLimits()
        setLimits(l)
        chunkBytes = l.chunkBytes
      } catch (err: any) {
        fail(errorText(err, 'The upload service could not be reached. Please try again.'), 'restart')
        return
      }
    }

    setUploadState('uploading')
    setError(null)
    setRecovery('none')
    rateSamplesRef.current = []
    setProgress({ bytesSent: 0, bytesTotal: file.size, rate: null })

    const upload = createInstallerUpload(
      file,
      { filename: file.name, filetype: file.type, appName: jobSettings().app_name },
      chunkBytes,
      {
        onProgress: noteProgress,
        onSuccess: (uploadId) => {
          uploadIdRef.current = uploadId
          void submitFromUpload(uploadId)
        },
        onError: uploadFailed,
      },
    )
    uploadRef.current = upload
    upload.start()
  }

  // Try again: resume the upload from the service's offset, or re-submit the job, or start over
  const handleTryAgain = () => {
    setError(null)
    if (recovery === 'resume' && uploadRef.current) {
      setUploadState('uploading')
      setRecovery('none')
      rateSamplesRef.current = []
      uploadRef.current.start()
    } else if (recovery === 'resubmit' && uploadIdRef.current) {
      void submitFromUpload(uploadIdRef.current)
    } else {
      uploadRef.current = null
      uploadIdRef.current = null
      setUploadState('idle')
      setProgress(null)
      setRecovery('none')
    }
  }

  // Cancel: the upload is deleted on the service and the dialog is back at the file step
  const handleCancelUpload = async () => {
    const upload = uploadRef.current
    uploadRef.current = null
    uploadIdRef.current = null
    setUploadState('idle')
    setProgress(null)
    setError(null)
    setRecovery('none')
    if (upload) {
      try {
        await upload.abort(true)
      } catch {
        // the service's cleanup removes what a failed termination left
      }
    }
  }

  // Navigation handlers
  const handleViewJob = () => {
    if (success?.jobId) {
      navigate('/settings/jobs', { state: { highlightJobId: success.jobId } })
    }
    onClose()
  }

  const handleViewApp = () => {
    onClose()
  }

  const handleClose = () => {
    if (uploadState === 'uploading' || uploadState === 'submitting') {
      // Confirm before closing during upload
      if (!window.confirm('Upload in progress. Are you sure you want to cancel?')) {
        return
      }
      void handleCancelUpload()
    } else if (uploadState === 'error' && recovery === 'resume') {
      // An upload the user gives up on is deleted rather than left for the expiry sweep
      void handleCancelUpload()
    }
    onClose()
  }

  // Keyboard accessibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return

      if (e.key === 'Escape') {
        handleClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, uploadState, file, appName])

  if (!isOpen) return null

  // Check if form is valid for submission
  const isFormValid = file && (!isZip || (zip.status === 'ready' && !!installerEntry)) && (
    packagingMode === 'ai'
      ? appName.trim()
      : (manualParams.appName.trim() && manualParams.appVersion.trim() && manualParams.installParams.trim())
  )
  // While the upload runs the inputs are disabled, not hidden: the user sees what was entered
  const isBusy = uploadState === 'uploading' || uploadState === 'submitting'
  const percent = progress && progress.bytesTotal > 0 ? Math.min(100, Math.floor((progress.bytesSent / progress.bytesTotal) * 100)) : 0
  const remainingSeconds = progress && progress.rate ? (progress.bytesTotal - progress.bytesSent) / progress.rate : null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-theme-card rounded-lg shadow-xl border border-theme-border-primary max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-theme-border-primary flex-shrink-0">
            <h2 className="text-lg font-semibold text-theme-text-primary">
              {success ? 'Upload Complete' : targetApp ? `Upload new version of ${targetApp.name}` : 'Upload Installer'}
            </h2>
            <button
              onClick={handleClose}
              className="text-theme-text-tertiary hover:text-theme-text-primary transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1">
            {targetApp && !success && (
              <p className="mb-4 rounded-lg border border-theme-border-primary bg-theme-secondary px-4 py-3 text-sm text-theme-text-secondary">
                The current version is {targetApp.version || 'unknown'}. The installer must be the same
                product and a newer version; it is packaged and tested before it replaces the current one.
              </p>
            )}
            {success ? (
              // Success state
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-theme-text-primary mb-2">Package Added to Catalog</h3>
                <p className="text-theme-text-secondary mb-4">
                  The app has been added to your catalog with a <span className="text-yellow-600 dark:text-yellow-400">Pending</span> status.
                  Packaging is now in progress.
                </p>
                <div className="bg-theme-tertiary rounded-lg p-3 mb-4 space-y-2">
                  <div>
                    <p className="text-xs text-theme-text-tertiary">File</p>
                    <p className="text-theme-text-primary font-mono text-sm truncate">{file?.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-theme-text-tertiary">App Name</p>
                    <p className="text-theme-text-primary font-medium text-sm">
                      {packagingMode === 'ai' ? appName : manualParams.appName}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-theme-text-tertiary">Mode</p>
                    <p className="text-theme-text-primary font-medium text-sm">
                      {packagingMode === 'ai' ? 'AI-Powered' : 'Manual Configuration'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-theme-text-tertiary">Job ID</p>
                    <p className="text-theme-text-primary font-mono text-sm truncate">{success.jobId}</p>
                  </div>
                </div>
                <p className="text-sm text-theme-text-tertiary mb-4">
                  {packagingMode === 'ai'
                    ? 'This process typically takes 5-15 minutes.'
                    : 'Manual mode packages are typically faster (1-3 minutes).'
                  } The app status will update to <span className="text-green-600 dark:text-green-400">Ready</span> once complete.
                </p>
              </div>
            ) : (
              // Form state
              <>
                {/* File Drop Zone */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Installer File
                  </label>

                  {!file ? (
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={handleBrowseClick}
                      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                        isDragOver
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-theme-border-primary hover:border-theme-border-secondary hover:bg-theme-secondary/50'
                      }`}
                    >
                      <CloudArrowUpIcon className={`w-12 h-12 mx-auto mb-3 ${isDragOver ? 'text-blue-500' : 'text-theme-text-tertiary'}`} />
                      <p className="text-theme-text-primary mb-1">
                        {isDragOver ? 'Drop file here' : 'Drag and drop installer file'}
                      </p>
                      <p className="text-sm text-theme-text-tertiary mb-3">or click to browse</p>
                      <p className="text-xs text-theme-text-tertiary">
                        Supported: {supportedExtensions.join(', ')}{limits ? ` (max ${formatFileSize(limits.maxBytes)})` : ''}
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-theme-tertiary rounded-lg border border-theme-border-primary">
                      <div className="flex items-center gap-3 min-w-0">
                        <DocumentIcon className="w-10 h-10 text-blue-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-theme-text-primary font-medium truncate">{file.name}</p>
                          <p className="text-sm text-theme-text-tertiary">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={handleRemoveFile}
                        disabled={isBusy}
                        className="p-1 text-theme-text-tertiary hover:text-red-500 transition-colors flex-shrink-0 disabled:opacity-50 disabled:hover:text-theme-text-tertiary"
                        title="Remove file"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={supportedExtensions.join(',')}
                    onChange={handleFileInputChange}
                    className="hidden"
                  />

                  {zip.status === 'reading' && (
                    <p className="mt-3 text-sm text-theme-text-tertiary">Reading the zip...</p>
                  )}
                  {zip.status === 'unusable' && (
                    <p role="alert" className="mt-3 text-sm text-red-500">{zip.message}</p>
                  )}
                  {zip.status === 'ready' && (
                    <fieldset className="mt-4" disabled={isBusy}>
                      <legend className="block text-sm font-medium text-theme-text-secondary mb-2">
                        Choose the installer
                      </legend>
                      <div className="max-h-48 overflow-y-auto rounded-lg border border-theme-border-primary divide-y divide-theme-border-primary">
                        {zip.candidates.map((candidate) => (
                          <label key={candidate.name} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-theme-secondary/50">
                            <input
                              type="radio"
                              name="zip-installer"
                              value={candidate.name}
                              checked={installerEntry === candidate.name}
                              onChange={() => setInstallerEntry(candidate.name)}
                            />
                            <span className="min-w-0 flex-1 text-sm text-theme-text-primary break-all">{candidate.name}</span>
                            <span className="text-xs text-theme-text-tertiary flex-shrink-0">{formatFileSize(candidate.size)}</span>
                          </label>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-theme-text-tertiary">
                        This is the file that runs. Include only what it needs in the zip: every file is delivered to each device.
                      </p>
                    </fieldset>
                  )}
                </div>

                {/* Packaging Mode Toggle */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-theme-text-secondary mb-3">
                    Packaging Mode
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setPackagingMode('ai')}
                      disabled={isBusy}
                      className={`flex-1 p-3 rounded-lg border-2 transition-colors text-left ${
                        packagingMode === 'ai'
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-theme-border-primary hover:border-theme-border-secondary'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">🤖</span>
                        <span className={`font-medium ${packagingMode === 'ai' ? 'text-blue-600 dark:text-blue-400' : 'text-theme-text-primary'}`}>
                          AI Mode
                        </span>
                        <span className="text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded">Recommended</span>
                      </div>
                      <p className="text-xs text-theme-text-tertiary">
                        Claude AI analyzes installer and auto-detects settings
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPackagingMode('manual')}
                      disabled={isBusy}
                      className={`flex-1 p-3 rounded-lg border-2 transition-colors text-left ${
                        packagingMode === 'manual'
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-theme-border-primary hover:border-theme-border-secondary'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">⚙️</span>
                        <span className={`font-medium ${packagingMode === 'manual' ? 'text-purple-400' : 'text-theme-text-primary'}`}>
                          Manual Mode
                        </span>
                      </div>
                      <p className="text-xs text-theme-text-tertiary">
                        You provide app details and install parameters
                      </p>
                    </button>
                  </div>
                </div>

                {packagingMode === 'ai' ? (
                  // AI Mode fields
                  <>
                    {/* App Name Input */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                        Package name
                      </label>
                      <input
                        type="text"
                        value={appName}
                        onChange={(e) => setAppName(e.target.value)}
                        placeholder="e.g., My Enterprise App"
                        className="w-full px-3 py-2.5 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="mt-2 text-xs text-theme-text-tertiary">
                        This name will be displayed in your app catalog
                      </p>
                    </div>

                    {/* Install Timeout Setting */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                        Installation Timeout (seconds)
                      </label>
                      <input
                        type="number"
                        value={installTimeout || ''}
                        onChange={(e) => setInstallTimeout(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                        placeholder="600 (default)"
                        min={60}
                        max={3600}
                        className="w-full px-3 py-2.5 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="mt-2 text-xs text-theme-text-tertiary">
                        Increase for large installers like Adobe Reader (60-3600 seconds)
                      </p>
                    </div>

                    {/* Agentic Analysis Option */}
                    {/* <div className="mb-6">
                      <label className="flex items-center justify-between cursor-pointer">
                        <div>
                          <span className="text-sm font-medium text-theme-text-secondary">
                            AI-Powered Analysis
                          </span>
                          <p className="text-xs text-theme-text-tertiary mt-1">
                            Use Claude AI to analyze and optimize package configuration
                          </p>
                        </div>
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={enableAgentic}
                            onChange={(e) => setEnableAgentic(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-theme-tertiary peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </div>
                      </label>
                    </div> */}
                  </>
                ) : (
                  // Manual Mode fields
                  <>
                    {/* Required Fields */}
                    <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                      <p className="text-purple-700 dark:text-purple-300 text-sm font-medium mb-1">Required Fields</p>
                      <p className="text-purple-700 dark:text-purple-300/70 text-xs">
                        App Name, Version, and Install Parameters are required for manual mode.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {/* App Name */}
                      <div>
                        <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                          Package name <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={manualParams.appName}
                          onChange={(e) => updateManualParam('appName', e.target.value)}
                          placeholder="e.g., MyApp"
                          className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                        />
                      </div>

                      {/* App Version */}
                      <div>
                        <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                          Version <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={manualParams.appVersion}
                          onChange={(e) => updateManualParam('appVersion', e.target.value)}
                          placeholder="e.g., 1.0.0"
                          className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    {/* Install Parameters */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                        Silent Install Parameters <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={manualParams.installParams}
                        onChange={(e) => updateManualParam('installParams', e.target.value)}
                        placeholder="e.g., /S or /qn /norestart"
                        className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      />
                      <p className="mt-1 text-xs text-theme-text-tertiary">
                        Parameters for silent installation (e.g., /S for NSIS, /qn for MSI)
                      </p>
                    </div>

                    {/* Publisher (Optional) */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                        Publisher
                      </label>
                      <input
                        type="text"
                        value={manualParams.appPublisher}
                        onChange={(e) => updateManualParam('appPublisher', e.target.value)}
                        placeholder="e.g., Contoso Inc."
                        className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      />
                    </div>

                    {/* Advanced Options Toggle */}
                    <button
                      type="button"
                      onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                      className="flex items-center gap-2 text-sm text-theme-text-secondary hover:text-theme-text-primary mb-4"
                    >
                      {showAdvancedOptions ? (
                        <ChevronUpIcon className="w-4 h-4" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4" />
                      )}
                      Advanced Options
                    </button>

                    {showAdvancedOptions && (
                      <div className="space-y-4 mb-4 p-4 bg-theme-tertiary rounded-lg">
                        {/* Installer Type: for a zip, the chosen file decides it */}
                        <div hidden={isZip}>
                          <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                            Installer Type
                          </label>
                          <select
                            value={manualParams.installerType}
                            onChange={(e) => updateManualParam('installerType', e.target.value as any)}
                            className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                          >
                            <option value="auto">Auto-detect</option>
                            <option value="exe">EXE</option>
                            <option value="msi">MSI</option>
                            <option value="msix">MSIX</option>
                          </select>
                        </div>

                        {/* Uninstall Method */}
                        <div>
                          <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                            Uninstall Method
                          </label>
                          <select
                            value={manualParams.uninstallMethod}
                            onChange={(e) => updateManualParam('uninstallMethod', e.target.value as any)}
                            className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                          >
                            <option value="uninstall_string">Registry Uninstall String</option>
                            <option value="msi_product_code">MSI Product Code</option>
                            <option value="custom">Custom Uninstaller</option>
                          </select>
                        </div>

                        {/* Product Code (for MSI) */}
                        {manualParams.uninstallMethod === 'msi_product_code' && (
                          <div>
                            <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                              MSI Product Code {chosenIsMsi ? (
                                <span className="text-theme-text-tertiary">(optional: read from the MSI)</span>
                              ) : <span className="text-red-400">*</span>}
                            </label>
                            <input
                              type="text"
                              value={manualParams.productCode}
                              onChange={(e) => updateManualParam('productCode', e.target.value)}
                              placeholder="{XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX}"
                              className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm font-mono"
                            />
                          </div>
                        )}

                        {/* Uninstall Parameters */}
                        <div>
                          <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                            Uninstall Parameters
                          </label>
                          <input
                            type="text"
                            value={manualParams.uninstallParams}
                            onChange={(e) => updateManualParam('uninstallParams', e.target.value)}
                            placeholder="e.g., /S or /qn"
                            className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                          />
                        </div>

                        {/* Detection Type */}
                        <div>
                          <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                            Detection Method
                          </label>
                          <select
                            value={manualParams.detectionType}
                            onChange={(e) => updateManualParam('detectionType', e.target.value as any)}
                            className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                          >
                            <option value="registry">Registry</option>
                            <option value="file">File Path</option>
                            <option value="msi_product_code">MSI Product Code</option>
                          </select>
                        </div>

                        {/* Close Apps */}
                        <div>
                          <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                            Close Applications Before Install
                          </label>
                          <input
                            type="text"
                            value={manualParams.closeApps}
                            onChange={(e) => updateManualParam('closeApps', e.target.value)}
                            placeholder="e.g., notepad,chrome (comma-separated)"
                            className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                          />
                          <p className="mt-1 text-xs text-theme-text-tertiary">
                            Process names to close before installation (without .exe)
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Package Info */}
                <div className="mb-6 p-3 bg-theme-tertiary rounded-lg">
                  <p className="text-xs text-theme-text-secondary">
                    <span className="font-medium text-theme-text-primary">Included:</span> PSADT Package + Intune Package (.intunewin)
                  </p>
                </div>

                <PackagingEntitlementNotice />

                {/* Info */}
                <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-blue-700 dark:text-blue-300 text-sm">
                    {packagingMode === 'ai'
                      ? 'Upload your installer file and we\'ll create deployment-ready PSADT and Intune packages. The packaging process typically takes 5-15 minutes.'
                      : 'Manual mode lets you specify exact installation parameters. Useful when you know the silent install switches or have specific requirements.'
                    }
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Footer: progress and errors sit next to the buttons, where the user is looking */}
          <div className="p-4 border-t border-theme-border-primary flex-shrink-0 space-y-3">
            {!success && isBusy && progress && (
              <div data-testid="upload-progress">
                <div className="flex items-center justify-between mb-2 text-sm">
                  <span className="text-theme-text-secondary">
                    {uploadState === 'submitting' ? 'Creating the packaging job...' : 'Uploading...'}
                  </span>
                  <span className="text-theme-text-primary font-medium">{percent}%</span>
                </div>
                <div className="w-full bg-theme-tertiary rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2 text-xs text-theme-text-tertiary">
                  <span>{formatFileSize(progress.bytesSent)} of {formatFileSize(progress.bytesTotal)}</span>
                  <span>
                    {progress.rate ? `${formatFileSize(progress.rate)}/s` : ''}
                    {remainingSeconds !== null ? ` · about ${formatDuration(remainingSeconds)} remaining` : ''}
                  </span>
                </div>
              </div>
            )}

            {!success && error && (
              <div ref={errorRef} role="alert" className="p-3 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-700 rounded-lg">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
                    {isEntitlementRefusal(error) && <EntitlementRefusalAction />}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3">
              {success ? (
                <>
                  <button
                    onClick={handleViewJob}
                    className="px-4 py-2 text-theme-text-secondary hover:text-theme-text-primary transition-colors"
                  >
                    View Job Status
                  </button>
                  <button
                    onClick={handleViewApp}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    View Catalog
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={isBusy ? handleCancelUpload : handleClose}
                    className="px-4 py-2 text-theme-text-secondary hover:text-theme-text-primary transition-colors"
                  >
                    Cancel
                  </button>
                  {uploadState === 'error' && recovery !== 'none' ? (
                    <button
                      onClick={handleTryAgain}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      {recovery === 'restart' ? 'Start over' : 'Try again'}
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={isBusy || !isFormValid}
                      className={`px-4 py-2 ${packagingMode === 'ai' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'} disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center`}
                    >
                      {isBusy ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {uploadState === 'submitting' ? 'Submitting...' : 'Uploading...'}
                        </>
                      ) : (
                        'Start Packaging'
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function to format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// "about 5 minutes", "about 40 seconds", "about 2 hours"
function formatDuration(seconds: number): string {
  if (seconds < 60) return plural(Math.max(1, Math.round(seconds)), 'second')
  if (seconds < 3600) return plural(Math.round(seconds / 60), 'minute')
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.round((seconds % 3600) / 60)
  return minutes ? `${hours} h ${minutes} min` : plural(hours, 'hour')
}
